import ArrowBack from '@mui/icons-material/ArrowBack';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { FormattedMessage, useIntl } from 'react-intl';
import ContentView from '~/components/ContentView';
import RouteSchedule from '~/components/RouteSchedule';
import messages from './messages';

import { Link, getRouteApi } from '@tanstack/react-router';
import RouteForm, { type RouteFormSubmitValues } from '~/components/RouteForm';
import { useDepots } from '~/hooks/api/useDepots';
import { useRoute } from '~/hooks/api/useRoute';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import DeleteRouteButton from './DeleteRouteButton';
import DisableRouteButton from './DisableRouteButton';
import ActivateRouteButton from './ActivateRouteButton';
import { useEditRoute } from '~/hooks/api/useEditRoute';

const routeApi = getRouteApi('/trolley-routes/$routeId');

const RouteEdit = () => {
  const { routeId } = routeApi.useParams();
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const routeEditMutation = useEditRoute(parseInt(routeId, 10), {
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveSuccess),
      });
    },
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.saveFailure),
      });
    },
  });

  const handleSubmit = (values: RouteFormSubmitValues) => {
    const {
      assignmentRange,
      assignmentRangeFirstShift,
      assignmentRangeLastShift,
      enableAssignmentRangeFirstShift,
      enableAssignmentRangeLastShift,
    } = values;

    routeEditMutation.mutate({
      name: values.name,
      meetingPlace: values.meetingPlace,
      depotId: values.depot,
      evenNumberOfParticipants: values.evenNumberOfParticipants,
      keepRouteStaffed: values.keepRouteStaffed,
      brotherRequirement: values.brotherRequirement,
      assignmentRange: assignmentRange,
      assignmentRangeFirstShift:
        enableAssignmentRangeFirstShift && assignmentRangeFirstShift
          ? assignmentRangeFirstShift
          : null,
      assignmentRangeLastShift:
        enableAssignmentRangeLastShift && assignmentRangeLastShift
          ? assignmentRangeLastShift
          : null,
    });
  };

  const routeIdNum = parseInt(routeId, 10);
  const { data: route, isLoading } = useRoute({ id: routeIdNum });
  const { data: depots, isLoading: depotsIsLoading } = useDepots();
  const isFetching = isLoading || depotsIsLoading;
  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="back"
              component={Link}
              to="/trolley-routes"
              sx={{ mr: 2 }}
              size="large"
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ flex: 1 }}>
              <FormattedMessage {...messages.title} />
            </Typography>
            {route && !route.isSuspended && (
              <>
                <DisableRouteButton routeId={route.id} />
                <DeleteRouteButton routeId={routeIdNum} />
              </>
            )}
          </Toolbar>
        </AppBar>
      }
      isLoading={isFetching}
    >
      {!isFetching && depots && (
        <>
          {route && route.isSuspended && (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <ActivateRouteButton routeId={route.id} />
            </Box>
          )}
          {!route?.isSuspended && (
            <>
              <RouteForm
                onSubmit={handleSubmit}
                initialValues={{
                  name: route?.name,
                  meetingPlace: route?.meetingPlace,
                  depot: route?.depotId?.toString(),
                  evenNumberOfParticipants: route?.evenNumberOfParticipants,
                  keepRouteStaffed: route?.keepRouteStaffed,
                  brotherRequirement: (route?.brotherRequirement ?? []).map(
                    (band) => ({
                      threshold: band.threshold ?? 1,
                      brotherCount: band.brotherCount ?? 1,
                    })
                  ),
                  assignmentRange: {
                    minPublishers: route?.assignmentRange?.minPublishers ?? 1,
                    maxPublishers: route?.assignmentRange?.maxPublishers ?? 1,
                  },
                  assignmentRangeFirstShift:
                    route?.assignmentRangeFirstShift?.minPublishers != null
                      ? {
                          minPublishers:
                            route.assignmentRangeFirstShift.minPublishers,
                          maxPublishers:
                            route.assignmentRangeFirstShift.maxPublishers ?? 1,
                        }
                      : null,
                  assignmentRangeLastShift:
                    route?.assignmentRangeLastShift?.minPublishers != null
                      ? {
                          minPublishers:
                            route.assignmentRangeLastShift.minPublishers,
                          maxPublishers:
                            route.assignmentRangeLastShift.maxPublishers ?? 1,
                        }
                      : null,
                }}
                availableDepots={depots.data}
              />
              <Box sx={{ p: 3 }}>
                <Divider />
              </Box>
              <RouteSchedule routeId={routeIdNum} />
            </>
          )}
        </>
      )}
    </ContentView>
  );
};

export default RouteEdit;
